/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import useForm from "../../../hooks/useForm";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import Form from "../../../components/forms/Form";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { fixEquipmentFields } from "./fields/fixEquipmentFields";
import useSelectOptions from "../../../hooks/useSelectOptions";
import { useCallback, useEffect, useRef } from "react";
import useBaseService from "../../../hooks/services/useBaseService";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import PageHeader from "../../../components/PageHeader";
import { Box } from "@mui/material";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import { entities } from "../../../constants/entities";
import ItemListSection from "../../../components/forms/ItemListSection";
import { useAttachEquipment } from "./hook/useAttachEquipment";
import useNotification from "../../../hooks/useNotification";
import { useRelabelEquipment } from "./hook/useRelabelEquipment";
import { EquipmentEnum } from "../../../enums/equipment-enum";

const FixEquipmentEnum = {
  REASSIGN_THE_ASSET_TAG: "reassign_the_asset_tag", // Retombar
  ATTACH_SAME_EQUIPMENT_TAG: "attach_same_equipment_tag", // Atrelar
};

const FixEquipment = () => {
  const navigate = useNavigate();

  const { showNotification } = useNotification();
  const { get: baseServiceGet, formErrors } = useBaseService(navigate);

  const [
    fixEquipmentSection,
    attachFixEquipmentSection,
    // * Referente a seção de retombar
    relabelProductSection,
    relabelEquipmentSection,
  ] = fixEquipmentFields;

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([fixEquipmentSection]),
    [fixEquipmentSection],
  );

  // * Lógica para atrelar equipamentos
  const {
    attachFormData,
    handleAttachChange,
    setAttachFormData,
    equipments,
    handleEquipmentsChange,
    handleSaveEquipment,
    initializeEquipmentForm,
    renderEquipmentFormBody,
    getEquipmentDisplayValue,
    createAttachment,
    setActionEquipment,
  } = useAttachEquipment({
    formData,
    setFormData,
  });

  const situationOptions = useSelectOptions({
    fieldId: "equipment.situation",
    formData,
    setFormData,
    rawOptions: [
      {
        value: FixEquipmentEnum.REASSIGN_THE_ASSET_TAG,
        label: "Retombar",
      },
      {
        value: FixEquipmentEnum.ATTACH_SAME_EQUIPMENT_TAG,
        label: "Atrelar",
      },
    ],
    mapToOption: (option) => ({ value: option.value, label: option.label }),
    isMulti: false,
  });

  useEffect(() => {
    setFormData({
      equipment: {
        situation: {
          value: FixEquipmentEnum.REASSIGN_THE_ASSET_TAG,
          label: "Retombar",
        },
      },
    });
  }, []);

  const situationRef = useRef(situationOptions.selectedValue?.value);

  const equipmentOptions = useRemoteSelectOptions({
    queryKey: ["equipments"],
    baseServiceGet,
    entityPath: entities.equipments.get,
    extraFilters: {
      status_id: { $eq: EquipmentEnum.AVAILABLE },
      is_completed: { $eq: 1 },
    },
    defaultSort: {
      column: "number",
      order: "desc",
    },
    columnDataBase: ["number", "name"],
    fieldId: "equipment.equipment_id",
    isMulti: false,
    mapToOption: (equipment) => ({
      value: equipment.id,
      label: `${equipment.number} - ${equipment.name}`,
      raw: equipment,
    }),
    refetchWhen: () => {
      if (situationRef.current !== situationOptions.selectedValue?.value) {
        situationRef.current = situationOptions.selectedValue?.value;
        return true;
      }
      return false;
    },
    formData,
    setFormData,
    perPage: 15,
    enablePagination: true,
  });

  const {
    // * Seção de produto
    relabelProductFormData,
    relabelProductChange,
    setRelabelProductFormData,

    // * Seção de equipamentos
    relabelEquipmentFormData,
    relabelEquipmentChange,
    setRelabelEquipmentFormData,

    // * Mutations
    createRelabel,

    // * Form
    renderRelabelPrductFormBody,
    renderRelabelEquipmentFormBody,
  } = useRelabelEquipment({
    formData,
    setFormData,
    equipmentOptions,
  });

  const selects = {
    "equipment.situation": situationOptions,
    "equipment.equipment_id": equipmentOptions,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  async function handleSubmit() {
    switch (formData.equipment?.situation?.value) {
      case "reassign_the_asset_tag":
        try {
          // * Lógica de submit para retombar o equipamento
          const cleanedProductFormDataRelabel = cleanPayload(
            relabelProductFormData,
          );
          const cleanedEquipmentFormDataRelabel = cleanPayload(
            relabelEquipmentFormData,
          );

          if (!cleanedEquipmentFormDataRelabel?.equipment?.number) {
            showNotification(
              "warning",
              "É necessário preencher todos os campos para retombar o equipamento.",
            );
            return;
          }

          const payloadRelabel = {
            productItem: {
              condition_id:
                cleanedProductFormDataRelabel.productItem.condition_id,
            },
            equipment: {
              number: cleanedEquipmentFormDataRelabel.equipment.number,
              serial_number: String(
                cleanedEquipmentFormDataRelabel.equipment.serial_number,
              ),
            },
          };

          await createRelabel.mutateAsync(payloadRelabel);
        } catch (error) {
          console.error("Erro ao retomar equipamento:", {
            status: error.status,
            message: error.message,
          });
        }
        break;
      case "attach_same_equipment_tag":
        try {
          // * Lógica de submit para atrelar o equipamentos
          const cleanedFormData = cleanPayload(formData);

          const payload = {
            equipments: cleanedFormData.attached_equipment?.map(
              (item) => item.equipment_id.value,
            ),
          };

          if (!payload.equipments?.length) {
            showNotification(
              "warning",
              "É necessário adicionar ao menos um equipamento para ser atrelado.",
            );
            return;
          }

          await createAttachment.mutateAsync({
            equipmentId: formData.equipment.equipment_id.value,
            payload,
          });
        } catch (error) {
          console.error("Erro ao atrelar equipamentos:", {
            status: error.status,
            message: error.message,
          });
        }
        break;
      default:
        // Lógica padrão ou mensagem de erro
        break;
    }
  }

  const showAttachEquipmentSection =
    formData.equipment?.situation?.value ===
    FixEquipmentEnum.ATTACH_SAME_EQUIPMENT_TAG;

  const showRelabelEquipmentSection =
    formData.equipment?.situation?.value ===
    FixEquipmentEnum.REASSIGN_THE_ASSET_TAG;

  return (
    <MainLayout>
      <PageHeader title="Corrigir Equipamento" showBackButton={false} />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Corrigir"
          textLoadingSubmit="Corrigindo..."
          handleBack={() => {}}
          disabledSubmitButton={
            !formData.equipment?.situation || !formData.equipment?.equipment_id
          }
          isLoading={false}>
          {() => {
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div key={fixEquipmentSection.section}>
                  <SectionHeader section={fixEquipmentSection.section} />
                  <SimpleBody
                    fields={fixEquipmentSection.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />
                </div>

                {showAttachEquipmentSection && (
                  <div key={attachFixEquipmentSection.section}>
                    <SectionHeader
                      section={attachFixEquipmentSection.section}
                    />
                    <ItemListSection
                      section={attachFixEquipmentSection.section}
                      fields={attachFixEquipmentSection.fields}
                      initialData={equipments}
                      onItemsChange={handleEquipmentsChange}
                      renderFormBody={renderEquipmentFormBody}
                      getItemDisplayValue={getEquipmentDisplayValue}
                      itemFormData={attachFormData}
                      onItemChange={handleAttachChange}
                      itemFormErrors={formErrors}
                      onFormSubmit={handleSaveEquipment}
                      initializeItemForm={initializeEquipmentForm}
                      isDisabledButton={!formData.equipment.equipment_id?.value}
                      captureAction={setActionEquipment}
                    />
                  </div>
                )}

                {showRelabelEquipmentSection && (
                  <>
                    <div key={relabelProductSection.section}>
                      <SectionHeader section={relabelProductSection.section} />
                      {renderRelabelPrductFormBody()}
                    </div>

                    <div key={relabelEquipmentSection.section}>
                      <SectionHeader
                        section={relabelEquipmentSection.section}
                      />
                      {renderRelabelEquipmentFormBody()}
                    </div>
                  </>
                )}
              </Box>
            );
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default FixEquipment;
